/**
 * Geometry utility functions for battlemap drawings
 * Handles bounds calculation for different shape types
 */

import type { DrawingPath, DrawingShape } from "@/features/battlemap/types";

/**
 * Bounding box representation
 */
export interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Extract bounding box from a drawing shape
 */
export function getDrawingBounds(
  drawing: DrawingPath | DrawingShape
): BoundingBox {
  const baseBounds: BoundingBox = {
    minX: 0,
    maxX: 0,
    minY: 0,
    maxY: 0,
  };

  // Rect and Ellipse use x, y, width, height
  if (drawing.type === "rect" || drawing.type === "ellipse") {
    return {
      minX: drawing.x,
      maxX: drawing.x + drawing.width,
      minY: drawing.y,
      maxY: drawing.y + drawing.height,
    };
  }

  // Text uses approximations based on font size
  if (drawing.type === "text") {
    const fs = drawing.fontSize || 24;
    const approxW = drawing.width || (drawing.content?.length || 0) * fs * 0.6;
    const approxH = fs * 1.2;
    return {
      minX: drawing.x,
      maxX: drawing.x + approxW,
      minY: drawing.y,
      maxY: drawing.y + approxH,
    };
  }

  // Polygon and Brush use points array
  if (drawing.type === "polygon" || drawing.type === "brush") {
    const pts = drawing.points || [];
    if (pts.length === 0) {
      return baseBounds;
    }
    return computePointsBounds(pts);
  }

  return baseBounds;
}

/**
 * Compute bounding box from an array of points [x1, y1, x2, y2, ...]
 */
export function computePointsBounds(points: number[]): BoundingBox {
  if (points.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }

  return { minX, maxX, minY, maxY };
}

/**
 * Compute combined bounds for multiple drawings
 */
export function computeCombinedBounds(
  drawings: (DrawingPath | DrawingShape)[]
): BoundingBox | null {
  if (drawings.length === 0) {
    return null;
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const drawing of drawings) {
    const bounds = getDrawingBounds(drawing);
    minX = Math.min(minX, bounds.minX);
    maxX = Math.max(maxX, bounds.maxX);
    minY = Math.min(minY, bounds.minY);
    maxY = Math.max(maxY, bounds.maxY);
  }

  if (minX === Infinity) {
    return null;
  }

  return { minX, maxX, minY, maxY };
}

/**
 * Convert bounds to rect parameters {x, y, width, height}
 */
export function boundsToRect(bounds: BoundingBox) {
  return {
    x: bounds.minX,
    y: bounds.minY,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY,
  };
}

/**
 * Normalize rect coordinates (ensure positive width/height)
 */
export function normalizeRect(
  startX: number,
  startY: number,
  endX: number,
  endY: number
) {
  return {
    x: Math.min(startX, endX),
    y: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  };
}

/**
 * Check if a point is inside a bounding box
 */
export function pointInBounds(
  x: number,
  y: number,
  bounds: BoundingBox
): boolean {
  return (
    x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY
  );
}
